import React, { useState } from 'react';
import axios from 'axios';
import { User, Phone, Mail, Building2, CreditCard, Upload, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

function DriverProfileTab({ driverData, driverId, onUpdate }) {
  const [formData, setFormData] = useState({
    name: driverData.driver.name || '',
    email: driverData.driver.email || '',
    username: driverData.user.username || '',
    phone_number: driverData.user.phone_number || '',
    bank_account: driverData.user.bank_account || '',
    interbancario_bank_account: driverData.user.interbancario_bank_account || '',
    bank_name: driverData.user.bank_name || '',
    name_on_bank_account: driverData.user.name_on_bank_account || ''
  });

  const [profileImage, setProfileImage] = useState(driverData.user.profile_image);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  function handleInputChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  function handleImageSelect(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen debe ser menor a 5MB');
      return;
    }

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  }

  async function handleImageUpload() {
    if (!imagePreview) return;

    try {
      setUploadingImage(true);

      const response = await axios.post(
        `${API_URL}/api/admin/drivers/${driverId}/upload-image`,
        { image_data: imagePreview }
      );

      setProfileImage(response.data.image_url);
      setImagePreview(null);
      alert('Imagen actualizada correctamente');
      onUpdate();
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleSaveProfile() {
    try {
      setSaving(true);

      await axios.put(`${API_URL}/api/admin/drivers/${driverId}`, formData);

      alert('Perfil actualizado correctamente');
      onUpdate();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Profile Image Section */}
      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 font-montserrat mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-[#B1336E]" />
          Foto de Perfil
        </h3>

        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Current/Preview Image */}
          <div className="flex-shrink-0">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Preview"
                className="w-32 h-32 rounded-full object-cover border-4 border-[#B1336E] shadow-lg"
              />
            ) : profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-slate-300 shadow-lg"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-[#B1336E] flex items-center justify-center border-4 border-slate-300 shadow-lg">
                <User className="w-16 h-16 text-white" />
              </div>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex-1">
            <label className="block">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageSelect}
                className="hidden"
              />
              <div className="px-4 py-2 bg-white border-2 border-slate-300 rounded-lg font-semibold font-montserrat text-slate-700 hover:border-[#B1336E] hover:text-[#B1336E] transition-colors cursor-pointer text-center">
                Seleccionar Imagen
              </div>
            </label>
            <p className="text-xs text-slate-500 font-montserrat mt-2">
              JPG, PNG o WebP. Máximo 5MB.
            </p>

            {imagePreview && (
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleImageUpload}
                  disabled={uploadingImage}
                  className="px-4 py-2 bg-[#67A43C] text-white rounded-lg font-semibold font-montserrat hover:bg-[#67A43C]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {uploadingImage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Subir Imagen
                    </>
                  )}
                </button>
                <button
                  onClick={() => setImagePreview(null)}
                  className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg font-semibold font-montserrat hover:bg-slate-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 font-montserrat mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-[#B1336E]" />
          Información Personal
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Nombre Completo
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
              placeholder="Nombre del conductor"
            />
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Usuario
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
              placeholder="Usuario del sistema"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#B1336E]" />
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
              placeholder="correo@ejemplo.com"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2 flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#B1336E]" />
              Teléfono
            </label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
              placeholder="999 999 999"
            />
          </div>
        </div>
      </div>

      {/* Bank Information */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 font-montserrat mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[#B1336E]" />
          Información Bancaria
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Bank Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#B1336E]" />
              Banco
            </label>
            <input
              type="text"
              name="bank_name"
              value={formData.bank_name}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
              placeholder="Nombre del banco"
            />
          </div>

          {/* Name on Account */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Titular de la Cuenta
            </label>
            <input
              type="text"
              name="name_on_bank_account"
              value={formData.name_on_bank_account}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
              placeholder="Nombre del titular"
            />
          </div>

          {/* Bank Account */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Número de Cuenta
            </label>
            <input
              type="text"
              name="bank_account"
              value={formData.bank_account}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
              placeholder="0000-0000-0000-000000"
            />
          </div>

          {/* Interbank Account */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
              Cuenta Interbancaria (CCI)
            </label>
            <input
              type="text"
              name="interbancario_bank_account"
              value={formData.interbancario_bank_account}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
              placeholder="00000000000000000000"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveProfile}
          disabled={saving}
          className="px-6 py-3 bg-[#B1336E] text-white rounded-lg font-bold font-montserrat hover:bg-[#B1336E]/90 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Guardar Cambios
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default DriverProfileTab;